import { useState, useEffect, useMemo } from "react";
import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  Stack,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import { TouchApp } from "@mui/icons-material";
import moment from "moment";
import ApiCalls from "services/flick_erp_api_call";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { base_url } from "services";
import { useMaterialUIController } from "context";
import Typography from "@mui/material/Typography";

export function WorkFromHome() {
  const user = JSON.parse(localStorage.getItem("user"));
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const palette = theme.palette;

  const [wfhRequest, setWfhRequest] = useState({
    date: moment().format("YYYY-MM-DD"),
    reason: "",
  });
  const [employee, setEmployee] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [wfhRows, setWfhRows] = useState([]);
  const [editingRequest, setEditingRequest] = useState(null);

  const ui = {
    surface: darkMode ? palette.background.card : colors.white.main,
    soft: darkMode ? palette.background.muted : colors.background.elevated,
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.14),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.secondary.main,
    hero: darkMode
      ? `linear-gradient(180deg, ${rgba(palette.background.default, 0.98)} 0%, ${rgba(
          palette.background.card,
          0.94
        )} 100%)`
      : `linear-gradient(180deg, ${rgba(colors.light.main, 0.94)} 0%, ${rgba(
          colors.white.main,
          0.98
        )} 100%)`,
    panel: darkMode
      ? `linear-gradient(135deg, ${rgba(palette.background.card, 0.98)} 0%, ${rgba(
          palette.background.muted,
          0.96
        )} 100%)`
      : `linear-gradient(135deg, ${colors.background.default} 0%, ${colors.background.accent} 100%)`,
  };

  useEffect(() => {
    getMyWFHRequests();
    getEmployee();
  }, []);

  const getEmployee = async () => {
    const res = await ApiCalls.getEmployee(user.employee_id);
    if (res.success && res.code === 200) {
      setEmployee(res.data || {});
    }
  };

  const getMyWFHRequests = async () => {
    try {
      const res = await ApiCalls.getWFHRequests();
      if (res.success && res.code === 200) {
        const filteredData = [...(res.data || [])].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setWfhRows(filteredData);
      }
    } catch (error) {
      console.error("Error fetching WFH requests:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setWfhRequest((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenApplyDialog = (request = null) => {
    if (request) {
      setEditingRequest(request);
      setWfhRequest({
        date: request.date || request?.shift_detail?.date || "",
        reason: request.reason || "",
      });
    } else {
      setEditingRequest(null);
      setWfhRequest({
        date: moment().format("YYYY-MM-DD"),
        reason: "",
      });
    }
    setApplyDialogOpen(true);
  };

  const handleCloseApplyDialog = () => {
    setApplyDialogOpen(false);
    setEditingRequest(null);
    setWfhRequest({
      date: moment().format("YYYY-MM-DD"),
      reason: "",
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const requestData = {
        employee: user.employee_id,
        date: wfhRequest.date,
        reason: wfhRequest.reason,
      };

      const response = editingRequest
        ? await ApiCalls.updateWFHRequest(editingRequest.id, requestData)
        : await ApiCalls.createWFHRequest(requestData, false);

      if (response.success) {
        await getMyWFHRequests();
        handleCloseApplyDialog();
      }
    } catch (error) {
      console.error("Error submitting WFH request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      const response = await ApiCalls.deleteWFHRequest(requestId);
      if (response.success) {
        await getMyWFHRequests();
      }
    } catch (error) {
      console.error("Error deleting WFH request:", error);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      PENDING: { color: "warning", label: "Pending" },
      APPROVED: { color: "success", label: "Approved" },
      REJECTED: { color: "error", label: "Rejected" },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <MDButton size="small" variant="contained" color={config.color}>{config.label}</MDButton>;
  };

  const tableData = useMemo(
    () => ({
      columns: [
        {
          Header: "Date",
          accessor: "date",
          Cell: ({ row }) => (
            <Typography variant="body2" fontWeight="medium">
              {row.original?.shift_detail?.date || row.original?.date}
            </Typography>
          ),
        },
        {
          Header: "Reason",
          accessor: "reason",
          Cell: ({ row }) => (
            <Typography
              variant="body2"
              sx={{
                maxWidth: 320,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.original.reason}
            </Typography>
          ),
        },
        {
          Header: "Status",
          accessor: "status",
          Cell: ({ row }) => getStatusChip(row.original.status),
        },
        {
          Header: "Applied On",
          accessor: "created_at",
          Cell: ({ row }) => (
            <Typography variant="body2">
              {moment(row.original.created_at).format("DD MMM YYYY")}
            </Typography>
          ),
        },
        {
          Header: "Team Lead Approval",
          accessor: "team_lead_approved",
          Cell: ({ row }) => (
            <Typography variant="body2">
              {row.original.team_lead_approved ? "Approved" : row.original.status === "PENDING" ? "Pending" : "Rejected"}
            </Typography>
          ),
        },
        {
          Header: "HR Approval",
          accessor: "hr_approved",
          Cell: ({ row }) => (
            <Typography variant="body2">
              {row.original.hr_approved ? "Approved" : row.original.status === "PENDING" ? "Pending" : "Rejected"}
            </Typography>
          ),
        },
        {
          Header: "Actions",
          accessor: "actions",
          Cell: ({ row }) => (
            <Stack direction="row" spacing={1}>
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                disabled={row.original.status === "APPROVED" || row.original.status === "REJECTED"}
                onClick={() => handleOpenApplyDialog(row.original)}
              >
                Edit
              </MDButton>
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                disabled={row.original.status === "APPROVED" || row.original.status === "REJECTED"}
                onClick={() => handleDeleteRequest(row.original.id)}
              >
                Delete
              </MDButton>
            </Stack>
          ),
        },
      ],
      rows: wfhRows,
    }),
    [wfhRows]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={3} sx={{ borderBottom: `1px solid ${ui.border}`, background: ui.hero }}>
        <MDBox
          mb={3}
          sx={{
            p: { xs: 3, md: 1.5 },
            borderRadius: "28px",
            background: `linear-gradient(135deg, ${rgba(colors.primary.main, 0.98)} 0%, ${rgba(colors.info.focus, 0.95)} 55%, ${rgba(colors.info.main, 0.88)} 100%)`,
            color: colors.white.main,
          }}
        >
          <Grid container spacing={3} p={1}>
            <Grid item xs={12} lg={7} sx={{ display: "flex", alignItems: "center" }}>
              <MDBox sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                <MDBox mt={2}>
                  <MDAvatar
                    variant="square"
                    src={employee?.img ? (employee?.img.includes("http") ? employee?.img : base_url + employee?.img) : ""}
                    alt={`${employee?.user?.firstname}${employee?.user?.lastname}` || "profile-image"}
                    size="xl"
                    shadow="sm"
                    sx={{ borderRadius: "16px" }}
                  />
                </MDBox>
                <MDBox sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
                  <MDTypography variant="h4" color="white" mt={2}>
                    {employee?.user?.firstname} {employee?.user?.lastname}
                  </MDTypography>
                  <MDTypography variant="body2" color="white" sx={{ opacity: 0.82, maxWidth: 620 }}>
                    Apply for remote work days, keep approvals visible, and track the full request lifecycle from one place.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={12} lg={5} mt={2}>
              <Grid container spacing={2} justifyContent="end">
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2.25, backgroundColor: rgba(colors.white.main, 0.12), border: `1px solid ${rgba(colors.white.main, 0.14)}` }}>
                    <MDTypography variant="caption" sx={{ color: rgba(colors.white.main, 0.72) }}>
                      TOTAL REQUESTS
                    </MDTypography>
                    <MDTypography variant="h5" color="white" mt={0.5}>
                      {wfhRows.length}
                    </MDTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2.25, backgroundColor: rgba(colors.white.main, 0.12), border: `1px solid ${rgba(colors.white.main, 0.14)}` }}>
                    <MDTypography variant="caption" sx={{ color: rgba(colors.white.main, 0.72) }}>
                      APPROVED
                    </MDTypography>
                    <MDTypography variant="h5" color="white" mt={0.5}>
                      {wfhRows.filter((item) => item.status === "APPROVED").length}
                    </MDTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2.25, backgroundColor: rgba(colors.white.main, 0.12), border: `1px solid ${rgba(colors.white.main, 0.14)}` }}>
                    <MDTypography variant="caption" sx={{ color: rgba(colors.white.main, 0.72) }}>
                      PENDING
                    </MDTypography>
                    <MDTypography variant="h5" color="white" mt={0.5}>
                      {wfhRows.filter((item) => item.status === "PENDING").length}
                    </MDTypography>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox
          sx={{
            padding: "20px",
            background: ui.panel,
            borderRadius: "28px",
            border: `1px solid ${ui.border}`,
            boxShadow: darkMode ? "0 20px 40px rgba(2, 6, 23, 0.28)" : "none",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
            spacing={2}
            mb={3}
            mt={2}
          >
            <MDBox style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <MDBox
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "16px",
                  display: "grid",
                  placeItems: "center",
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
                  color: colors.white.main,
                }}
              >
                <TouchApp />
              </MDBox>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold">
                  Work From Home
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: ui.body }}>
                  Apply, review, and track your remote-work requests in one consistent workspace.
                </MDTypography>
              </MDBox>
            </MDBox>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} width={{ xs: "100%", lg: "auto" }}>
              <MDButton
                color="info"
                onClick={() => handleOpenApplyDialog()}
                sx={{ height: 55, px: 2.25, borderRadius: "14px", color: colors.white.main, whiteSpace: "nowrap" }}
              >
                Create WFH Request
              </MDButton>
            </Stack>
          </Stack>

          <MDBox pt={1}>
            <DataTable table={tableData} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
          </MDBox>
        </MDBox>

        <Dialog
          PaperProps={{
            sx: {
              backgroundColor: ui.surface,
              boxShadow: darkMode ? "0 24px 48px rgba(2, 6, 23, 0.42)" : "none",
              width: "600px",
              maxWidth: "none",
              borderRadius: "20px",
              border: `1px solid ${ui.border}`,
              backgroundImage: "none",
            },
          }}
          open={applyDialogOpen}
          onClose={handleCloseApplyDialog}
        >
          <DialogTitle
            sx={{
              width: "100%",
              p: "20px",
              background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.focus} 100%)`,
              color: "white",
            }}
          >
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
              <Typography sx={{ color: "white", fontWeight: "Medium" }}>
                {editingRequest ? "Edit Work From Home Request" : "Apply for Work From Home"}
              </Typography>
              <Icon fontSize="medium" onClick={handleCloseApplyDialog}>
                close
              </Icon>
            </div>
          </DialogTitle>
          <DialogContent sx={{ p: "15px" }}>
            <Card
              sx={{
                overflow: "visible",
                padding: "15px",
                background: darkMode
                  ? `linear-gradient(180deg, ${palette.background.card} 0%, ${palette.background.muted} 100%)`
                  : `linear-gradient(180deg, ${colors.light.main} 0%, ${colors.background.accent} 100%)`,
                border: `1px solid ${ui.border}`,
              }}
            >
              <MDBox component="form" pb={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="WFH Date"
                      type="date"
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={wfhRequest.date}
                      onChange={(e) => handleInputChange("date", e.target.value)}
                      helperText="WFH can only be applied for one day at a time."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      variant="standard"
                      fullWidth
                      multiline
                      rows={5}
                      value={wfhRequest.reason}
                      onChange={(e) => handleInputChange("reason", e.target.value)}
                      placeholder="Please provide a clear reason for working from home."
                    />
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <MDButton variant="outlined" color="secondary" onClick={handleCloseApplyDialog}>
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={isSubmitting || !(wfhRequest.date && wfhRequest.reason)}>
              {isSubmitting ? "Submitting..." : editingRequest ? "Update Request" : "Submit Request"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default WorkFromHome;
